import { useEffect, useMemo, useState } from 'react';

import { shouldShowWidgetBranding } from './branding';
import { ChatWidget } from './ChatWidget';
import { CloseGlyph } from './components/CloseGlyph';
import { PoweredBy } from './components/PoweredBy';
import {
  ChannelLauncher,
  closedLauncherPresentation,
  openWidgetChannel,
  resolveWidgetChannels,
} from './channels';
import type { WidgetChannel } from './channels/types';
import {
  loadGrowthWidgetConfig,
  resolveBootstrapApiUrl,
} from './api';
import { findEmbedScript } from './embed-script';
import type { PublicGrowthWidgetConfig } from './types';

type GrowthWidgetProps = {
  publicId: string;
};

const LAUNCHER_SIZE_PX = {
  small: 48,
  medium: 56,
  large: 64,
} as const;

const CONFIG_REFRESH_INTERVAL_MS = 15 * 60_000;

export function GrowthWidget({ publicId }: GrowthWidgetProps) {
  const [config, setConfig] = useState<PublicGrowthWidgetConfig | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [webchatOpen, setWebchatOpen] = useState(false);
  const [webchatKey, setWebchatKey] = useState<string | null>(null);
  const [displayAllowed, setDisplayAllowed] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const script = findEmbedScript();
    const apiUrl = resolveBootstrapApiUrl(script);

    async function load() {
      try {
        const bootstrap = await loadGrowthWidgetConfig(apiUrl, publicId);
        if (cancelled) return;
        if (!bootstrap.available || !bootstrap.config) {
          setConfig(null);
          setMenuOpen(false);
          setWebchatOpen(false);
          return;
        }
        setConfig(bootstrap.config);
      } catch {
        if (!cancelled) {
          setConfig(null);
          setMenuOpen(false);
          setWebchatOpen(false);
        }
      }
    }

    function refreshIfVisible() {
      if (document.visibilityState === 'visible') {
        void load();
      }
    }

    void load();
    const interval = window.setInterval(
      refreshIfVisible,
      CONFIG_REFRESH_INTERVAL_MS,
    );
    document.addEventListener('visibilitychange', refreshIfVisible);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [publicId]);

  const appearance = config?.configuration?.appearance;
  const behavior = config?.configuration?.behavior;
  const displayMode = behavior?.displayMode ?? 'both';

  useEffect(() => {
    function matchesDisplayMode(): boolean {
      if (displayMode === 'both') return true;
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      return displayMode === 'mobile' ? isMobile : !isMobile;
    }

    const media = window.matchMedia('(max-width: 768px)');
    const update = () => setDisplayAllowed(matchesDisplayMode());
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [displayMode]);

  const channels = useMemo(
    () => resolveWidgetChannels(config?.channels, { fallbackWebchat: false }),
    [config?.channels],
  );

  useEffect(() => {
    if (!webchatKey) return;
    const stillVisible = channels.some(
      (channel) => channel.webchatPublicKey === webchatKey,
    );
    if (!stillVisible) {
      setWebchatKey(null);
      setWebchatOpen(false);
    }
  }, [channels, webchatKey]);

  useEffect(() => {
    if (!config || !behavior?.openAutomatically || channels.length === 0) {
      return;
    }
    const delay = Math.max(0, behavior.delayMs ?? 0);
    const timer = window.setTimeout(() => {
      if (channels.length === 1 && channels[0]?.type === 'webchat') {
        openWebChat(channels[0]);
        return;
      }
      setMenuOpen(true);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [config?.publicId, channels.length, behavior?.openAutomatically, behavior?.delayMs]);

  const positionClass =
    appearance?.position === 'bottom-left' ? 'rw-pos-left' : 'rw-pos-right';
  const primaryColor = appearance?.primaryColor || '#448AFF';
  const launcherSize =
    LAUNCHER_SIZE_PX[appearance?.launcherSize ?? 'medium'] ?? 56;
  const launcherIconKey = appearance?.launcherIconKey ?? 'message-bubble';
  const isSingle = channels.length === 1;
  const showPoweredBy = shouldShowWidgetBranding(config);
  const closedLauncher = closedLauncherPresentation(channels, {
    launcherIconKey,
    customIconUrl: appearance?.icon,
  });

  function openWebChat(channel: WidgetChannel) {
    const key = channel.webchatPublicKey?.trim();
    if (!key) return;
    setWebchatKey(key);
    setWebchatOpen(true);
    setMenuOpen(false);
  }

  function handleLauncherClick() {
    if (!channels.length) return;
    if (isSingle) {
      openWidgetChannel(channels[0]!, { openWebChat });
      return;
    }
    setMenuOpen((open) => !open);
  }

  if (!config || channels.length === 0 || !displayAllowed) {
    return null;
  }

  return (
    <div
      className={['rw-root', positionClass, showPoweredBy ? 'rw-has-watermark' : '']
        .filter(Boolean)
        .join(' ')}
      style={{
        ['--rw-primary' as string]: primaryColor,
        ['--rw-launcher-size' as string]: `${launcherSize}px`,
        ['--rw-offset-x' as string]: `${appearance?.offsetX ?? 20}px`,
        ['--rw-offset-y' as string]: `${appearance?.offsetY ?? 20}px`,
        ['--rw-radius' as string]: `${appearance?.borderRadius ?? 16}px`,
      }}
    >
      {webchatKey ? (
        <ChatWidget
          widgetId={webchatKey}
          hideLauncher
          panelOpen={webchatOpen}
          onPanelOpenChange={(open) => {
            setWebchatOpen(open);
            if (!open) setMenuOpen(false);
          }}
        />
      ) : null}

      {isSingle ? (
        <div className="rw-launcher-motion">
          <button
            type="button"
            className={['rw-launcher', webchatOpen ? 'rw-launcher-open' : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => {
              if (webchatOpen) {
                setWebchatOpen(false);
                setMenuOpen(false);
                return;
              }
              handleLauncherClick();
            }}
            aria-label={
              webchatOpen
                ? 'Close'
                : appearance?.label || channels[0]?.displayName || config.name
            }
            style={
              !webchatOpen && closedLauncher.background
                ? { background: closedLauncher.background }
                : undefined
            }
          >
            {webchatOpen ? (
              <CloseGlyph />
            ) : (
              <span
                className="rw-launcher-icon"
                dangerouslySetInnerHTML={{ __html: closedLauncher.iconHtml }}
              />
            )}
          </button>
        </div>
      ) : (
        <ChannelLauncher
          channels={channels}
          open={menuOpen}
          chatOpen={webchatOpen}
          unread={0}
          customLauncherIcon={closedLauncher.imageUrl}
          launcherIconHtml={closedLauncher.iconHtml}
          animationClass=""
          onToggleMenu={handleLauncherClick}
          onOpenWebChat={(channel) => {
            if (channel) openWebChat(channel);
          }}
          onClose={() => {
            setMenuOpen(false);
            setWebchatOpen(false);
          }}
        />
      )}
      {showPoweredBy ? <PoweredBy variant="launcher" /> : null}
    </div>
  );
}
